import { useIntl } from 'react-intl';
import { useState } from 'react';
import { useDisclosure } from '@/hooks';
import { Button, Col, Row, Table, Modal, Form, Space, Divider } from 'antd';
import {
  PaymentMethodCreateRequestDTO,
  PaymentMethodEnum,
  PaymentMethodTranslate,
} from '@/core/domain/invoice/PaymentMethod/PaymentMethod.types';
import {
  useDeletePaymentMethodInvoice,
  usePaymentMethods,
} from '@/core/domain/invoice/PaymentMethod/PaymentMethod.hook';
import { useRouteMatch } from 'react-router-dom';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { DeleteIcon, EditIcon } from '@/components/_common';
import { ColumnsType } from 'antd/lib/table';
import NewPaymentModal from './NewPaymentModal';
import { quantityFormatter } from '@/utils/number';
import PaymentMethodTef from './PaymentMethodTef';

interface Props {
  disabledFields?: boolean;
}

export default function InvoicePaymentCondition({ disabledFields }: Props) {
  const intl = useIntl().formatMessage;
  const [form] = Form.useForm<PaymentMethodCreateRequestDTO>();

  const { invoiceId } = useRouteMatch<{ invoiceId: string }>().params;

  const translate = (message: string, type: 'column' | 'button' = 'column') =>
    intl({ id: `invoicePaymentCondition.${type}.${message}` });

  const [paymentId, setPaymentId] = useState<number | undefined>();
  const [paymentModalVisible, setPaymentModalVisible] = useDisclosure(false);

  const { data } = usePaymentMethods(Number(invoiceId));
  const paymentMethods = flatInfiniteData(data);

  const totaAlreadyPaid = paymentMethods.reduce(
    (acc, cur) => acc + cur.value,
    0,
  );

  const { mutateAsync: deleteMutateAsync } = useDeletePaymentMethodInvoice();

  const handleRemove = (id: number) => {
    Modal.confirm({
      title: 'Remover pagamento',
      content:
        'Deseja realmente remover o pagamento?Esta ação é irreversível e não será possível recuperar o pagamento posteriormente. Tem certeza de que deseja continuar?',
      onOk: async () => {
        await deleteMutateAsync({ invoiceId: Number(invoiceId), id });
      },
      okType: 'danger',
      okText: intl({ id: 'commons.entity.remove' }),
    });
  };

  const columns: ColumnsType<PaymentMethodCreateRequestDTO> = [
    {
      title: translate('paymentType'),
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (value: PaymentMethodEnum) => {
        return PaymentMethodTranslate[value];
      },
    },
    {
      title: translate('value'),
      dataIndex: 'value',
      key: 'value',
      render: (value) => quantityFormatter(value),
    },
    {
      title: translate('flagCard'),
      dataIndex: 'creditCardBrand',
      key: 'creditCardBrand',
      render: (value) => value ?? '-',
    },
    {
      title: translate('authorizationNumber'),
      dataIndex: 'authorizationNumber',
      key: 'authorizationNumber',
      render: (value) => value ?? '-',
    },
    {
      title: intl({
        id: 'commons.list.header.action',
      }),
      width: 100,
      fixed: 'right',
      align: 'right',
      render: (_, row) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => {
                setPaymentId(row.id);
                setPaymentModalVisible.toggle();
              },
            }}
            disabled={disabledFields}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemove(row.id)}
            disabled={disabledFields}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row gutter={[16, 16]} justify="end">
        <Col>
          <PaymentMethodTef />
        </Col>

        <Col>
          <Button
            type="primary"
            onClick={() => setPaymentModalVisible.toggle()}
            disabled={disabledFields}
          >
            {translate('newPayment', 'button')}
          </Button>
        </Col>

        <Col span={24}>
          <Table
            columns={columns}
            pagination={false}
            className="table-small"
            dataSource={paymentMethods}
          />
        </Col>
      </Row>

      <NewPaymentModal
        form={form}
        paymentId={paymentId}
        setPaymentId={setPaymentId}
        paymentModalVisible={paymentModalVisible}
        setPaymentModalVisible={setPaymentModalVisible.toggle}
        invoiceId={Number(invoiceId)}
        totaAlreadyPaid={totaAlreadyPaid}
      />
    </>
  );
}
